/**
 * Painel Berytus › Fontes: o token da rota de entrada de fontes (script enviar_fontes_berytus.py, escopo source_ingest)
 * e as cargas do catálogo mestre (CMED, ABCFarma, IQVIA, PharmaDB, bulas, Qualidoc) com o resumo de cada uma, o saldo do PharmaDB
 * (diário e mensal, de /v1/usage) e o último relatório da simulação encadeada (IQVIA → PharmaDB → CMED → ABCFarma).
 * O token aparece uma vez só, ao gerar; depois fica só o começo dele. Gerar e revogar: só superadmin.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';

interface Sources {
  pharmadb: { daily: Quota; monthly: Quota; checkedAt: string | null; usedToday: number; usedThisMonth: number; day: number; configured: boolean };
  chain: { id: number; createdAt: string; report: ChainReport } | null;
  chainCandidates: Partial<Record<ChainSource, number>>;
  notSeen: Record<string, number>;
  pending: { curation: number; blocks: number };
  tokens: { id: string; name: string; prefix: string; createdAt: string; createdBy: string | null; lastUsedAt: string | null; revokedAt: string | null }[];
  runs: { id: number; source: string; status: string; origin: string | null; expectedRows: number | null; sentRows: number | null;
    stats: Record<string, unknown>; error: string | null; startedAt: string; finishedAt: string | null; rawPrunedAt: string | null }[];
}
type ChainSource = 'iqvia' | 'pharmadb' | 'cmed' | 'abcfarma';
interface Quota { remaining: number; limit: number | null }
interface ChainSum { source: ChainSource; runId: number; items: number; created: number; updated: number; unchanged: number; conflicts: number; errors: number; curation: number;
  blocksPending: number; prices: number; relink: number; notSeen: number; fields: Record<string, number> }
interface ChainReport { runs: { source: ChainSource; runId: number }[]; perSource: ChainSum[]; seconds: number; at: string;
  total: Omit<ChainSum, 'source' | 'runId' | 'fields' | 'unchanged'> & { fichesChanged: number; fields: Record<string, Record<string, number>> } }
const SOURCE_NAME: Record<string, string> = { iqvia: 'IQVIA', pharmadb: 'PharmaDB', cmed: 'CMED', abcfarma: 'ABCFarma', pharmadb_bulas: 'Bulas PharmaDB' };
const STATUS: Record<string, { label: string; tone: string }> = {
  running: { label: 'Recebendo', tone: 'info' }, succeeded: { label: 'Concluída', tone: 'ok' }, failed: { label: 'Falhou', tone: 'err' },
};
const n = (v: unknown) => (typeof v === 'number' ? int(v) : null);

export function AdminSources() {
  const isSuper = useMe().user.platformRole === 'superadmin';
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'sources'], queryFn: () => api<Sources>('/admin/sources'), refetchInterval: 15_000 });
  const [name, setName] = useState('Script do Eduardo');
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ id: string; token: string }>('/admin/sources/tokens', { method: 'POST', json: { name } }),
    onSuccess: (r) => { setCreated(r.token); qc.invalidateQueries({ queryKey: ['admin', 'sources'] }); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/admin/sources/tokens/${id}/revoke`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sources'] }),
  });
  const act = useMutation({
    mutationFn: (v: { path: string; json?: unknown; method?: string }) => api(v.path, { method: v.method ?? 'POST', json: v.json ?? {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sources'] }),
  });
  const [diff, setDiff] = useState<number | null>(null);
  const d = q.data;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Fontes do catálogo mestre</h1>
          <p>Token da rota de entrada de fontes (IQVIA e PharmaDB enviados pelo script) e as cargas de cada fonte. A rota só grava nas tabelas
            brutas: nada é aplicado no catálogo mestre sem a simulação e a aprovação.</p>
        </div>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
        <div className="stack-y">
          <section className="panel stack-y" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>Tokens de entrada</h2>
            {isSuper && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
                <label className="small" style={{ display: 'grid', gap: 4 }}>Nome
                  <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <button type="button" className="btn primary" disabled={name.trim().length < 2 || create.isPending} onClick={() => create.mutate()}>Gerar token</button>
              </div>
            )}
            {create.error && <ErrorBox error={create.error} />}
            {created && (
              <div className="notice warn small">
                <b>Copie agora: o token não aparece de novo.</b> Ele fica no computador que roda o script (nunca em chat ou e-mail).
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <code style={{ wordBreak: 'break-all' }}>{created}</code>
                  <button type="button" className="btn sm" onClick={() => { void navigator.clipboard?.writeText(created); }}>Copiar</button>
                  <button type="button" className="btn sm quiet" onClick={() => setCreated(null)}>Fechar</button>
                </div>
              </div>
            )}
            {revoke.error && <ErrorBox error={revoke.error} />}
            {!d.tokens.length ? <p className="small muted" style={{ margin: 0 }}>Nenhum token gerado.</p> : (
              <div className="grid-wrap">
                <table className="simple">
                  <thead><tr><th>Nome</th><th>Começo</th><th>Gerado</th><th>Último uso</th><th>Situação</th><th /></tr></thead>
                  <tbody>
                    {d.tokens.map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td><code>{t.prefix}…</code></td>
                        <td className="small">{dateTime(t.createdAt)}{t.createdBy ? ` · ${t.createdBy}` : ''}</td>
                        <td className="small">{t.lastUsedAt ? dateTime(t.lastUsedAt) : '—'}</td>
                        <td>{t.revokedAt ? <Tag tone="off">Revogado {dateTime(t.revokedAt)}</Tag> : <Tag tone="ok">Ativo</Tag>}</td>
                        <td>{isSuper && !t.revokedAt && (
                          <button type="button" className="btn sm" disabled={revoke.isPending} onClick={() => { if (window.confirm(`Revogar o token "${t.name}"? O script para de conseguir enviar.`)) revoke.mutate(t.id); }}>Revogar</button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="panel stack-y" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>PharmaDB (leitura mensal)</h2>
            <p className="small muted" style={{ margin: 0 }}>
              {d.pharmadb.configured ? 'Chave configurada no servidor.' : <b>PHARMADB_API_KEY não configurada no .env do servidor.</b>}{' '}
              Saldo do plano (GET /v1/usage{d.pharmadb.checkedAt ? `, lido em ${dateTime(d.pharmadb.checkedAt)}` : ', ainda não lido'}):
              hoje <b>{int(d.pharmadb.daily.remaining)}</b>{d.pharmadb.daily.limit != null && ` de ${int(d.pharmadb.daily.limit)}`} ·
              no mês <b>{int(d.pharmadb.monthly.remaining)}</b>{d.pharmadb.monthly.limit != null && ` de ${int(d.pharmadb.monthly.limit)}`} (renova às 00:00 UTC, 21h de Brasília).
              Chamadas da Berytus: {int(d.pharmadb.usedToday)} hoje, {int(d.pharmadb.usedThisMonth)} no mês. Leitura a partir do dia {d.pharmadb.day} de cada mês.
              Só começa se o saldo de hoje e o do mês cobrem o necessário + 20%; em seguida, roda a simulação (nada muda no mestre).
            </p>
            {isSuper && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn sm" disabled={!d.pharmadb.configured || act.isPending}
                  onClick={() => act.mutate({ path: '/admin/sources/pharmadb/usage' })}>Consultar saldo</button>
                <button type="button" className="btn sm" disabled={act.isPending} onClick={() => {
                  const v = window.prompt('Dia do mês da leitura (1 a 28):', String(d.pharmadb.day));
                  if (v != null && /^\d+$/.test(v.trim())) act.mutate({ path: '/admin/sources/settings', method: 'PUT', json: { pharmadb_day: Number(v) } });
                }}>Mudar o dia</button>
                <button type="button" className="btn sm primary" disabled={!d.pharmadb.configured || act.isPending}
                  onClick={() => { if (window.confirm('Ler o PharmaDB agora? Usa requisições do plano.')) act.mutate({ path: '/admin/sources/pharmadb/run' }); }}>Ler agora</button>
              </div>
            )}
            <p className="small muted" style={{ margin: 0 }}>Na curadoria: {int(d.pending.curation)} divergência(s) regulatória(s) · {int(d.pending.blocks)} bloco(s) "pendente de revisão" (não bloqueiam nada).</p>
          </section>
          {act.error && <ErrorBox error={act.error} />}

          <Chain d={d} isSuper={isSuper} busy={act.isPending}
            reload={(src) => act.mutate({ path: `/admin/catalog/sources/${src}/run`, json: { force: src === 'cmed' } })} run={() => {
            const c = d.chainCandidates;
            const list = (['iqvia', 'pharmadb', 'cmed', 'abcfarma'] as const).map((k) => `${SOURCE_NAME[k]}: ${c[k] ? `#${c[k]}` : 'sem carga'}`).join(' → ');
            if (window.confirm(`Simular em sequência (${list})? Roda numa transação desfeita no fim: o mestre não muda. Pode levar alguns minutos.`)) act.mutate({ path: '/admin/sources/chain' });
          }} />

          <section className="panel flush">
            <div style={{ padding: '12px 16px 0' }}><h2 style={{ margin: 0 }}>Cargas</h2>
              <p className="small muted" style={{ margin: '4px 0 0' }}>Simular grava só o diff (o que mudaria, campo a campo). Aplicar é só depois da aprovação; passa pelo disjuntor e
                pode ser revertido pela carga.</p></div>
            <div className="grid-wrap">
              <table className="simple">
                <thead><tr><th>Carga</th><th>Fonte</th><th>Situação</th><th>Origem</th><th className="num">Esperadas</th><th className="num">Gravadas</th><th className="num">GTIN inválido</th><th className="num">Repetidas</th><th>Início</th><th>Simulação</th><th /></tr></thead>
                <tbody>
                  {d.runs.map((r) => (
                    <tr key={r.id}>
                      <td>#{r.id}</td>
                      <td>{SOURCE_NAME[r.source] ?? r.source}{r.rawPrunedAt && <div className="small muted" title="Ficam as 3 últimas cargas concluídas por fonte">bruta descartada</div>}</td>
                      <td><Tag tone={STATUS[r.status]?.tone ?? 'off'}>{STATUS[r.status]?.label ?? r.status}</Tag>{r.error && <div className="small" style={{ color: 'var(--err)' }}>{r.error}</div>}</td>
                      <td className="small" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.origin ?? ''}>{r.origin ?? '—'}</td>
                      <td className="num">{r.expectedRows != null ? int(r.expectedRows) : '—'}</td>
                      <td className="num">{n(r.stats.stored) ?? n(r.stats.staged) ?? n(r.stats.withEan) ?? '—'}</td>
                      <td className="num">{n(r.stats.invalid_gtin) ?? n(r.stats.invalidGtin) ?? '—'}</td>
                      <td className="num">{n(r.stats.duplicated) ?? '—'}</td>
                      <td className="small">{dateTime(r.startedAt)}</td>
                      <td className="small">{simText(r.stats)}</td>
                      <td>{isSuper && ['cmed', 'abcfarma', 'iqvia', 'pharmadb'].includes(r.source) && r.status === 'succeeded' && !r.rawPrunedAt && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button type="button" className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ path: `/admin/sources/runs/${r.id}/simulate` })}>Simular</button>
                          <button type="button" className="btn sm quiet" onClick={() => setDiff(r.id)}>Ver diff</button>
                          <button type="button" className="btn sm" disabled={act.isPending}
                            onClick={() => { if (window.confirm(`Aplicar a carga #${r.id} (${r.source}) no catálogo mestre? Só com a aprovação do Eduardo. Passa pelo disjuntor e pode ser revertida.`)) act.mutate({ path: `/admin/sources/runs/${r.id}/apply`, json: { confirm: true } }); }}>Aplicar</button>
                          {!!r.stats.application && (
                            <button type="button" className="btn sm quiet" disabled={act.isPending}
                              onClick={() => { if (window.confirm(`Reverter a aplicação da carga #${r.id}? Os campos voltam ao valor anterior (os que mudaram depois ficam).`)) act.mutate({ path: `/admin/sources/runs/${r.id}/revert` }); }}>Reverter</button>
                          )}
                        </div>
                      )}
                      {isSuper && r.source === 'pharmadb_bulas' && !r.rawPrunedAt && r.status !== 'running' && (
                        <button type="button" className="btn sm quiet" disabled={act.isPending}
                          onClick={() => { const reason = window.prompt(`Descartar a carga de bulas #${r.id}? As linhas brutas dela são apagadas para reenviar. Motivo:`); if (reason && reason.trim().length >= 5) act.mutate({ path: `/admin/sources/runs/${r.id}/discard`, json: { reason: reason.trim() } }); }}>Descartar</button>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          {diff != null && <Diff runId={diff} onClose={() => setDiff(null)} />}
        </div>
      )}
    </>
  );
}

/** Simulação encadeada (IQVIA → PharmaDB → CMED → ABCFarma, cada uma sobre a anterior, desfeita no fim): o último relatório. */
function Chain({ d, isSuper, busy, run, reload }: { d: Sources; isSuper: boolean; busy: boolean; run: () => void; reload: (src: 'cmed' | 'abcfarma') => void }) {
  const r = d.chain?.report;
  const fieldRows = r ? Object.entries(r.total.fields).flatMap(([src, f]) => Object.entries(f).map(([field, k]) => ({ src, field, k }))).sort((a, b) => b.k - a.k) : [];
  const notSeen = Object.entries(d.notSeen);
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Simulação encadeada da primeira carga</h2>
        <span className="grow" />
        {isSuper && (['cmed', 'abcfarma'] as const).map((src) => (
          <button key={src} type="button" className="btn sm quiet" disabled={busy} title="Lê a fonte de novo: grava a tabela bruta (e, como hoje, atualiza o mestre pela carga antiga)"
            onClick={() => { if (window.confirm(`Ler ${SOURCE_NAME[src]} de novo agora? Grava a tabela bruta e, como as leituras de hoje, também atualiza o catálogo mestre.`)) reload(src); }}>
            Ler {SOURCE_NAME[src]} de novo</button>
        ))}
        {isSuper && <button type="button" className="btn sm" disabled={busy} onClick={run}>Simular em sequência</button>}
      </div>
      <p className="small muted" style={{ margin: 0 }}>IQVIA → PharmaDB → CMED → ABCFarma, cada uma sobre o resultado da anterior, numa transação desfeita no fim: o mestre não muda.
        Usa a última carga concluída de cada fonte com tabela bruta{(['iqvia', 'pharmadb', 'cmed', 'abcfarma'] as const).some((k) => !d.chainCandidates[k])
          && <> (sem carga com bruta: <b>{(['iqvia', 'pharmadb', 'cmed', 'abcfarma'] as const).filter((k) => !d.chainCandidates[k]).map((k) => SOURCE_NAME[k]).join(', ')}</b>)</>}.{notSeen.length > 0 && <> Não vistas na última carga aplicada: {notSeen.map(([k, v]) => `${SOURCE_NAME[k] ?? k} ${int(v)}`).join(' · ')} (nada é apagado).</>}</p>
      {!r ? <p className="small muted" style={{ margin: 0 }}>Nenhuma simulação encadeada ainda.</p> : (
        <>
          <p className="small" style={{ margin: 0 }}>Rodada em {dateTime(d.chain!.createdAt)} ({int(r.seconds)} s): {r.runs.map((x) => `${SOURCE_NAME[x.source]} #${x.runId}`).join(' → ')}.</p>
          <div className="grid-wrap">
            <table className="simple">
              <thead><tr><th>Fonte</th><th className="num">Itens</th><th className="num">Fichas novas</th><th className="num">Atualizadas</th><th className="num">Sem mudança</th><th className="num">Campos</th>
                <th className="num">Conflitos de EAN</th><th className="num">Curadoria</th><th className="num">Pendente de revisão</th><th className="num">Não vistas</th><th className="num">SKUs a revincular</th></tr></thead>
              <tbody>
                {r.perSource.map((p) => (
                  <tr key={p.source}><td>{SOURCE_NAME[p.source]} #{p.runId}</td><td className="num">{int(p.items)}</td><td className="num">{int(p.created)}</td><td className="num">{int(p.updated)}</td><td className="num">{int(p.unchanged)}</td>
                    <td className="num">{int(Object.values(p.fields).reduce((a, b) => a + b, 0))}</td><td className="num">{int(p.conflicts)}</td><td className="num">{int(p.curation)}</td>
                    <td className="num">{int(p.blocksPending)}</td><td className="num">{int(p.notSeen)}</td><td className="num">{int(p.relink)}</td></tr>
                ))}
                <tr><td><b>Acumulado</b> ({int(r.total.fichesChanged)} fichas alteradas)</td><td className="num"><b>{int(r.total.items)}</b></td><td className="num"><b>{int(r.total.created)}</b></td>
                  <td className="num"><b>{int(r.total.updated)}</b></td><td className="num"><b>{int(r.perSource.reduce((a, p) => a + p.unchanged, 0))}</b></td><td className="num"><b>{int(fieldRows.reduce((a, b) => a + b.k, 0))}</b></td><td className="num"><b>{int(r.total.conflicts)}</b></td>
                  <td className="num"><b>{int(r.total.curation)}</b></td><td className="num"><b>{int(r.total.blocksPending)}</b></td><td className="num"><b>{int(r.total.notSeen)}</b></td><td className="num"><b>{int(r.total.relink)}</b></td></tr>
              </tbody>
            </table>
          </div>
          <details>
            <summary className="small">Campos alterados por fonte e campo ({int(fieldRows.length)})</summary>
            <table className="simple"><thead><tr><th>Fonte</th><th>Campo</th><th className="num">Fichas</th></tr></thead>
              <tbody>{fieldRows.map((x) => <tr key={`${x.src}.${x.field}`}><td>{SOURCE_NAME[x.src]}</td><td className="mono small">{x.field}</td><td className="num">{int(x.k)}</td></tr>)}</tbody></table>
          </details>
        </>
      )}
    </section>
  );
}

/** Resumo da última simulação (ou aplicação) guardado na carga. */
function simText(stats: Record<string, unknown>) {
  const s = (stats.application ?? stats.simulation) as Record<string, number | boolean | Record<string, number>> | undefined;
  if (!s) return '—';
  if (s.pendingApproval) return 'aplicação aguardando aprovação (Compliance)';
  const fields = Object.values((s.fields ?? {}) as Record<string, number>).reduce((a, b) => a + b, 0);
  return `${stats.application ? 'aplicada' : 'simulada'}${s.running ? ' (rodando…)' : ''}: ${int(Number(s.items ?? 0))} itens · ${int(Number(s.created ?? 0))} fichas novas · ${int(fields)} campos · `
    + `${int(Number(s.curation ?? 0))} curadoria · ${int(Number(s.blocksPending ?? 0))} blocos · ${int(Number(s.relink ?? 0))} SKUs a revincular`;
}

function Diff({ runId, onClose }: { runId: number; onClose: () => void }) {
  const [mode, setMode] = useState<'simulate' | 'apply'>('simulate');
  const q = useQuery({
    queryKey: ['admin', 'sources', 'diff', runId, mode],
    queryFn: () => api<{ byField: { field: string; action: string; n: number }[]; sample: { gtin14: string | null; field: string; old_value: unknown; new_value: unknown; action: string }[] }>(
      `/admin/sources/runs/${runId}/changes?mode=${mode}`),
  });
  const show = (v: unknown) => (v == null ? '—' : typeof v === 'string' ? v : JSON.stringify(v)).slice(0, 120);
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Diff da carga #{runId}</h2>
        <div className="seg" role="group" aria-label="Modo">
          {(['simulate', 'apply'] as const).map((m) => <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}>{m === 'simulate' ? 'Simulação' : 'Aplicação'}</button>)}
        </div>
        <span className="grow" />
        <button type="button" className="btn sm quiet" onClick={onClose}>Fechar</button>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.byField.length ? <p className="small muted">Nada registrado.</p> : (
        <div className="cols">
          <table className="simple">
            <thead><tr><th>Campo</th><th>Ação</th><th className="num">Fichas</th></tr></thead>
            <tbody>{q.data.byField.map((f) => <tr key={`${f.field}-${f.action}`}><td>{f.field === '_created' ? 'ficha nova' : f.field}</td><td>{f.action}</td><td className="num">{int(f.n)}</td></tr>)}</tbody>
          </table>
          <table className="simple">
            <thead><tr><th>EAN</th><th>Campo</th><th>Antes</th><th>Depois</th></tr></thead>
            <tbody>{q.data.sample.map((x, i) => <tr key={i}><td className="small">{x.gtin14}</td><td className="small">{x.field}{x.action !== 'set' ? ` (${x.action})` : ''}</td>
              <td className="small" style={{ maxWidth: 200, whiteSpace: 'normal' }}>{show(x.old_value)}</td><td className="small" style={{ maxWidth: 200, whiteSpace: 'normal' }}>{show(x.new_value)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
